<template>
  <div ref="container" class="mts-modal-form-select">
    <Input
      v-bind="getBindValue"
      v-model:value="state"
      :placeholder="props.disabled ? '-' : t('common.chooseText')"
      @click="props.readonly && showTableModal()"
      @keydown="handleKeydown"
    >
      <template v-for="item in Object.keys($slots)" #[item]="data">
        <slot :name="item" v-bind="{ ...data, label: state, disabled, showTableModal }"></slot>
      </template>
      <template #suffix="data">
        <slot name="suffix" v-bind="{ ...data, label: state, disabled, showTableModal }">
          <EllipsisOutlined
            :class="[{ '!cursor-not-allowed': disabled }]"
            @click.stop="() => !disabled && showTableModal()"
          />
          <PlusCircleFilled
            v-if="hasAddModal"
            :class="['text-14px', { 'text-[#01A887]': !disabled, '!cursor-not-allowed': disabled }]"
            @click.stop="() => !disabled && showAddModal()"
          />
        </slot>
      </template>
    </Input>

    <Form.ItemRest>
      <BasePageModal @register="registerModal" @ok="onOk" />
    </Form.ItemRest>
  </div>
</template>

<script setup lang="ts">
  import { EllipsisOutlined, PlusCircleFilled } from '@ant-design/icons-vue';
  import { Form, Input } from 'ant-design-vue';
  import { merge } from 'lodash-es';
  import { computed, ref, useAttrs, watch } from 'vue';
  import type { PageModal } from '@mts/render';
  import { useRuleFormItem } from '@mts-fe/hooks';
  import { isArray, omit } from '@mts-fe/utils';
  import { t } from '@mts/hooks/web/useI18n';
  import BasePageModal from '@mts/render/components/BasePageModal.vue';
  import { usePageModal } from '@mts/render/hooks/usePageModal';

  defineOptions({ name: 'ModalTableSelect', inheritAttrs: false });

  const props = withDefaults(
    defineProps<{
      value?: string | number;
      valueField?: string;
      separator?: string;
      readonly?: boolean;
      disabled?: boolean;
      allowClear?: boolean;
      modalConfig?: PageModal;
      hasAddModal?: boolean;
      addModalConfig?: PageModal;
      beforeShowModal?: () => PageModal | undefined;
    }>(),
    { valueField: 'value', readonly: true, allowClear: false },
  );
  const attrs = useAttrs();
  const getBindValue = computed(() => {
    return {
      ...attrs,
      ...omit(props, ['valueField', 'separator', 'modalConfig', 'hasAddModal', 'addModalConfig']),
    };
  });

  const emitData = ref<any[]>([]);
  // Embedded in the form, just use the hook binding to perform form verification
  const [state] = useRuleFormItem(props, 'value', 'change', emitData);

  const emit = defineEmits(['change', 'update:value', 'add', 'ok']);
  watch(
    () => state.value,
    (v) => {
      emit('update:value', v);
    },
  );

  const [registerModal, { showModal }] = usePageModal('ModalTableSelect');

  const showTableModal = () => {
    let modalConfig: PageModal | undefined = props.modalConfig;
    if (props.beforeShowModal) {
      modalConfig = props.beforeShowModal();
      if (!modalConfig) {
        return;
      }
    }
    if (props.separator) {
      const selectedRowKeys = props.value?.toString().split(props.separator).filter(Boolean) || [];

      merge(modalConfig, {
        table: {
          rowKey: modalConfig?.table?.rowKey || props.valueField,
          rowSelection: { selectedRowKeys },
        },
      });

      modalConfig!.table!.rowSelection!.selectedRowKeys = selectedRowKeys;
    }

    return modalConfig
      ? showModal({}, {}, modalConfig)
      : console.error('modalConfig is not available', modalConfig);
  };
  const showAddModal = () => {
    if (!props.addModalConfig) {
      emit('add');
      return;
    }
    return props.addModalConfig
      ? showModal({}, {}, props.addModalConfig)
      : console.error('addModalConfig is not available', props.addModalConfig);
  };

  const KEYDOWN_CODE = ['ArrowDown'];
  props.readonly && KEYDOWN_CODE.push('Space');
  const handleKeydown = (event: KeyboardEvent) =>
    KEYDOWN_CODE.includes(event.code) && showTableModal();

  const onOk = (data: Recordable | Recordable[]) => {
    console.log('ModalTableSelect', data);
    emitData.value = [data];
    if (!isArray(data)) {
      state.value = data[props.valueField];
      emit('ok', data);
    } else {
      if (props.separator) {
        state.value = data.map((i) => i[props.valueField]).join(props.separator);
      } else {
        emit('ok', data);
      }
    }
  };
</script>
<style>
  .ant-pagination-options-size-changer .ant-select-selector {
    border-color: #ccc !important;
  }
</style>
